import { MasterPreset } from "./masterPresets";
import { progressions } from "./chordUtils";
import {
  Step,
  SequencerProgressionState,
} from "../components/sequencer/types";
import { Register } from "./registerVoicing";
import {
  getCurrentFilterCutoffHz,
  getFilterType,
  getCurrentReverbMix,
  getReverbDecay,
  getCurrentDelayTime,
  getCurrentDelayFeedback,
  getCurrentDelayMix,
  getReverseMix,
  getAttack,
  getDecay,
  getSustain,
  getRelease,
  getAdsrEnabled,
} from "./audioUtils";

export interface CurrentStateRefs {
  getCurrentSampleId: () => string;
  getCurrentBpm: () => number;
  getCurrentKey: () => string;
  getCurrentProgression: () => string;
  getCurrentProgressionState?: () => SequencerProgressionState;
  getCurrentPatternPreset: () => string;
  getCurrentPattern: () => Step[];
  getCurrentStepsCount: () => number;
  getCurrentRegister: () => Register;
  getCurrentRibbonPositions: () => Array<{ startPercent: number; endPercent: number }>;
  getCurrentFxModes: () => {
    filter: boolean;
    reverb: boolean;
    delay: boolean;
    reverse: boolean;
  };
  getCurrentSequencerMode: () => "chord" | "drone-mono" | "drone-duo";
  getCurrentDroneNotes?: () => string[];
  getCurrentStrum: () => number;
  getCurrentGroove: () => number;
}

/**
 * Captures the current application state and returns a MasterPreset object
 * @param name The name for the preset
 * @param description Optional description for the preset
 * @param refs Object containing refs to get current state from various components
 * @returns A complete MasterPreset object
 */
export const captureCurrentState = (
  name: string,
  description: string,
  refs: CurrentStateRefs
): MasterPreset => {
  const timestamp = Date.now();
  const id = `custom_${timestamp}`;

  const sequencerMode = refs.getCurrentSequencerMode();
  const isDroneMode = sequencerMode === "drone-mono" || sequencerMode === "drone-duo";
  const progressionState = refs.getCurrentProgressionState?.();
  const progressionNameFromState =
    progressionState && progressionState.roman.length > 0
      ? Object.entries(progressions).find(([, candidate]) => {
          if (candidate.mode !== progressionState.mode) return false;
          if (candidate.roman.length !== progressionState.roman.length) {
            return false;
          }
          return candidate.roman.every(
            (roman, index) => roman === progressionState.roman[index]
          );
        })?.[0]
      : undefined;
  const progressionName =
    progressionState && progressionState.roman.length > 0
      ? progressionNameFromState || "custom"
      : refs.getCurrentProgression() || "custom";

  const preset: MasterPreset = {
    id,
    name,
    description,
    category: "custom",
    sample: {
      id: refs.getCurrentSampleId(),
    },
    pattern: {
      preset: refs.getCurrentPatternPreset(),
    },
    chord: {
      key: refs.getCurrentKey(),
      progression: progressionName,
      ...(progressionState && progressionState.roman.length > 0
        ? {
            roman: [...progressionState.roman],
            mode: progressionState.mode,
            customRoman: [...progressionState.roman],
            customMode: progressionState.mode,
          }
        : {}),
    },
    effects: {
      attack: getAttack(),
      decay: getDecay(),
      sustain: getSustain(),
      release: getRelease(),
      adsrEnabled: getAdsrEnabled(),
      filterCutoff: getCurrentFilterCutoffHz(),
      filterType: getFilterType(),
      reverbMix: getCurrentReverbMix(),
      reverbDecay: getReverbDecay(),
      delayTime: getCurrentDelayTime(),
      delayFeedback: getCurrentDelayFeedback(),
      delayMix: getCurrentDelayMix(),
      reverseMix: getReverseMix(),
    },
    sequencer: {
      bpm: refs.getCurrentBpm(),
      mode: sequencerMode,
    },
    stepsCount: refs.getCurrentStepsCount(),
    stepPattern: refs.getCurrentPattern(),
    register: refs.getCurrentRegister(),
    ribbonPositions: refs.getCurrentRibbonPositions(),
    fxModes: refs.getCurrentFxModes(),
    strum: refs.getCurrentStrum(),
    groove: refs.getCurrentGroove(),
  };

  // Add drone-specific fields if in drone mode
  if (isDroneMode && refs.getCurrentDroneNotes) {
    const droneNotes = refs.getCurrentDroneNotes();
    preset.droneMode = sequencerMode === "drone-mono" ? "mono" : "duo";
    preset.droneNotes = droneNotes;
  }

  return preset;
};
